import React from "react";
import { useSingleOktaUserQuery } from "../../../../../store/okta/userSlice";
import { useNavigate, useParams } from "react-router-dom";
import { formattedDateTime, hasPermission } from "../../../../../utils";
import { Status } from "../../../../atoms/status";
import { Divider } from "../../../../atoms/divider";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { Table } from "../../../../atoms/table";
import { Skeleton } from "../../../../atoms/skeleton";
import {
  PAGE_NAME,
  PERMISSIONS,
  ROUTE,
} from "../../../../../constants/constants";

const SingleOktaUser = () => {
  const pageClassName = "p-singleOktaUser";
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useSingleOktaUserQuery({ id });

  const appsTableHeader = [
    {
      title: "Serial",
      dataIndex: "id",
      isSortable: false,
    },
    {
      title: "Name",
      dataIndex: "name",
      isSortable: false,
    },
    {
      title: "Status",
      dataIndex: "status",
      isSortable: false,
    },
    {
      title: "Created",
      dataIndex: "created",
      isSortable: false,
    },
  ];

  const groupsTableHeader = [
    {
      title: "Serial",
      dataIndex: "id",
      isSortable: false,
    },
    {
      title: "Name",
      dataIndex: "name",
      isSortable: false,
    },
    {
      title: "Created",
      dataIndex: "created",
      isSortable: false,
    },
  ];

  const deviceTableHeader = [
    {
      title: "Serial",
      dataIndex: "id",
      isSortable: false,
    },
    {
      title: "Name",
      dataIndex: "name",
      isSortable: true,
    },
    {
      title: "Platform",
      dataIndex: "platform",
      isSortable: true,
    },
    {
      title: "Model",
      dataIndex: "model",
      isSortable: true,
    },
    {
      title: "Status",
      dataIndex: "deviceStatus",
      isSortable: true,
    },
    {
      title: "Number of Users",
      dataIndex: "numberOfUsers",
      isSortable: true,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      isSortable: true,
    },
  ];

  const profileUpdateHandler = () => {
    navigate(ROUTE.REQUEST_HISTORY + ROUTE.CREATE_REQUEST, {
      state: {
        type: { label: "User Profile", value: "User Profile" },
        subType: { label: "Update", value: "Update" },
        user: data?.result,
      },
    });
  };

  return (
    <div className={pageClassName}>
      <Breadcrumb />
      <p className={`${pageClassName}__title`}>{PAGE_NAME.OKTA_USER_INFO}</p>
      <div className={`${pageClassName}__container`}>
        {hasPermission(
          PERMISSIONS.REQUEST_MANAGEMENT_USER_PROFILE_UPDATE_REQUEST.title
        ) && (
            <div className={`${pageClassName}__container__request`}>
              <button
                className={`${pageClassName}__container__request__button`}
                onClick={profileUpdateHandler}
                disabled={isLoading}
              >
                Profile Update Request
              </button>
            </div>
          )}
        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            First Name
          </p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : (
              data?.result?.firstName ?? "-"
            )}
          </p>
        </div>
        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            Last Name
          </p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : (
              data?.result?.lastName ?? "-"
            )}
          </p>
        </div>
        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>Email</p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : (
              data?.result?.email ?? "-"
            )}
          </p>
        </div>
        {data?.result?.secondEmail && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Second Email
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {data?.result?.secondEmail}
            </p>
          </div>
        )}
        {data?.result?.mobilePhone && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Mobile Phone
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {data?.result?.mobilePhone}
            </p>
          </div>
        )}

        {data?.result?.pic && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Personal In Charge(PIC)
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {data?.result?.pic?.firstName} {data?.result?.pic?.lastName}
            </p>
          </div>
        )}

         {data?.result?.designation && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Designation
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {data?.result?.designation}
            </p>
          </div>
        )}

        {data?.result?.location && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Location
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {data?.result?.location}
            </p>
          </div>
        )}

        {data?.result?.organization && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Organization
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {data?.result?.organization?.name}
            </p>
          </div>
        )}

        {data?.result?.department?.department && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Department
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {data?.result?.department?.department}
            </p>
          </div>
        )}

        {data?.result?.section?.section && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Section
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {data?.result?.section?.section}
            </p>
          </div>
        )}


        {data?.result?.projects && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Projects
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {data?.result?.projects?.map((item: any) => {
                return (
                  <li
                    key={item.id}
                    className={`${pageClassName}__container__item__list`}
                  >
                    {item.name}
                  </li>
                );
              })}
            </p>
          </div>
        )}

        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>Status</p>
          {isLoading ? (
            <Skeleton height="small" />
          ) : data?.result?.status ? (
            <Status status={data?.result?.status} />
          ) : (
            "-"
          )}
        </div>
        {data?.result?.apps?.length !== 0 && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Number of Apps
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {isLoading ? (
                <Skeleton height="small" />
              ) : (
                data?.result?.apps?.length ?? 0
              )}
            </p>
          </div>
        )}

        {data?.result?.apps?.length !== 0 && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Number of Devices
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {isLoading ? (
                <Skeleton height="small" />
              ) : (
                data?.result?.devices?.length ?? 0
              )}
            </p>
          </div>
        )}
        {data?.result?.groups?.length !== 0 && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Number of Groups
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {isLoading ? (
                <Skeleton height="small" />
              ) : (
                data?.result?.groups?.length ?? 0
              )}
            </p>
          </div>
        )}
        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>Created</p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : data?.result?.created ? (
              formattedDateTime(data?.result?.created)
            ) : (
              "-"
            )}
          </p>
        </div>
        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            Activated
          </p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : data?.result?.activated ? (
              formattedDateTime(data?.result?.activated)
            ) : (
              "-"
            )}
          </p>
        </div>
        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            Last Login
          </p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : data?.result?.lastLogin ? (
              formattedDateTime(data?.result?.lastLogin)
            ) : (
              "-"
            )}
          </p>
        </div>
        {data?.result?.passwordChanged && (
          <div className={`${pageClassName}__container__item`}>
            <p className={`${pageClassName}__container__item__title`}>
              Password Changed
            </p>
            <p className={`${pageClassName}__container__item__value`}>
              {formattedDateTime(data?.result?.passwordChanged)}
            </p>
          </div>
        )}
        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            Status Changed
          </p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : data?.result?.statusChanged ? (
              formattedDateTime(data?.result?.statusChanged)
            ) : (
              "-"
            )}
          </p>
        </div>
        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            Last Updated
          </p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : data?.result?.lastUpdated ? (
              formattedDateTime(data?.result?.lastUpdated)
            ) : (
              "-"
            )}
          </p>
        </div>
        {data?.result?.apps?.length !== 0 && (
          <>
            <Divider />
            <div className={`${pageClassName}__apps`}>
              <p className={`${pageClassName}__apps__title`}>Apps</p>
              {/* <div className={`${pageClassName}__apps__container`}>
                {data?.result?.apps?.map((item: any, index: number) => (
                  <div
                    key={index}
                    className={`${pageClassName}__apps__container__item`}
                    onClick={() => {
                      navigate(`/app-data-list/${item.id}`);
                    }}
                  >
                    <img src={item.logo} alt={item.label} />
                    <div
                      className={`${pageClassName}__apps__container__item__info`}
                    >
                      <p
                        className={`${pageClassName}__apps__container__item__title`}
                      >
                        {item.label}
                      </p>
                      <p
                        className={`${pageClassName}__apps__container__item__value`}
                      >
                        <Status status={item.status} />
                      </p>
                    </div>
                  </div>
                ))}
              </div> */}

              <Table
                header={appsTableHeader}
                data={data?.result?.apps ?? []}
                loading={isLoading}
                firstLoad={isLoading}
                sortHandler={() => { }}
                startIndex={1}
                viewURL={ROUTE.OKTA_APP_LIST}
                cursorPointer
              />
            </div>
          </>
        )}
        {data?.result?.groups?.length !== 0 && (
          <>
            <Divider />
            <div className={`${pageClassName}__groups`}>
              <p className={`${pageClassName}__groups__title`}>Groups</p>
              <Table
                header={groupsTableHeader}
                data={data?.result?.groups ?? []}
                loading={isLoading}
                firstLoad={isLoading}
                sortHandler={() => { }}
                startIndex={1}
                viewURL={ROUTE.OKTA_GROUP_LIST}
                cursorPointer
              />
            </div>
          </>
        )}

        {data?.result?.devices?.length !== 0 && (
          <>
            <Divider />
            <div className={`${pageClassName}__groups`}>
              <p className={`${pageClassName}__groups__title`}>Devices</p>
              <Table
                header={deviceTableHeader}
                data={data?.result?.devices ?? []}
                loading={isLoading}
                firstLoad={isLoading}
                sortHandler={() => { }}
                startIndex={1}
                viewURL={ROUTE.OKTA_DEVICE_LIST}
                cursorPointer
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default SingleOktaUser;
